import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, FileText, Code, Database, BookOpen, ExternalLink } from 'lucide-react';

const courseMaterials = [
  {
    id: 1,
    title: "Course Slides & Presentations",
    description: "Complete slide decks for all 8 modules with detailed explanations and visual aids",
    type: "PDF",
    size: "45 MB",
    files: 8,
    icon: FileText,
    downloadUrl: "#",
    category: "Slides"
  },
  {
    id: 2,
    title: "Python Code Examples",
    description: "Jupyter notebooks with all code examples, exercises, and solutions",
    type: "ZIP",
    size: "12 MB",
    files: 25,
    icon: Code,
    downloadUrl: "#",
    category: "Code"
  },
  {
    id: 3,
    title: "Practice Datasets",
    description: "Real-world datasets for hands-on practice and project work",
    type: "CSV/JSON",
    size: "78 MB",
    files: 15,
    icon: Database,
    downloadUrl: "#",
    category: "Data"
  },
  {
    id: 4,
    title: "Reading Materials & References",
    description: "Curated list of research papers, articles, and additional reading materials",
    type: "PDF",
    size: "23 MB",
    files: 12,
    icon: BookOpen,
    downloadUrl: "#",
    category: "Reading"
  },
  {
    id: 5,
    title: "Assignment Templates",
    description: "Templates and starter code for all course assignments and projects",
    type: "ZIP",
    size: "8 MB",
    files: 10,
    icon: Code,
    downloadUrl: "#",
    category: "Templates"
  },
  {
    id: 6,
    title: "Cheat Sheets & Quick Reference",
    description: "Quick reference guides for algorithms, formulas, and Python libraries",
    type: "PDF",
    size: "5 MB",
    files: 6,
    icon: FileText,
    downloadUrl: "#",
    category: "Reference"
  }
];

const externalResources = [
  {
    title: "Scikit-learn Documentation",
    description: "Official documentation for the most popular ML library in Python",
    url: "https://scikit-learn.org/stable/",
    type: "Documentation"
  },
  {
    title: "Kaggle Learn",
    description: "Free micro-courses on machine learning and data science",
    url: "https://www.kaggle.com/learn",
    type: "Online Course"
  },
  {
    title: "Google's ML Crash Course",
    description: "Fast-paced, practical introduction to machine learning",
    url: "https://developers.google.com/machine-learning/crash-course",
    type: "Online Course"
  },
  {
    title: "Papers With Code",
    description: "Latest research papers with code implementations",
    url: "https://paperswithcode.com/",
    type: "Research"
  }
];

const getCategoryColor = (category: string) => {
  switch (category) {
    case 'Slides': return 'bg-blue-100 text-blue-700';
    case 'Code': return 'bg-green-100 text-green-700';
    case 'Data': return 'bg-purple-100 text-purple-700';
    case 'Reading': return 'bg-orange-100 text-orange-700';
    case 'Templates': return 'bg-yellow-100 text-yellow-700';
    case 'Reference': return 'bg-red-100 text-red-700';
    default: return 'bg-gray-100 text-gray-700';
  }
};

export const MaterialsSection = () => {
  const handleDownload = (material: any) => {
    // In a real application, this would trigger an actual download
    alert(`Downloading ${material.title}... (This is a demo)`);
  };

  return (
    <section id="materials" className="py-20 px-4 bg-white">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            Course <span className="text-blue-600">Materials</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Download comprehensive course materials including slides, code examples, datasets, and reference guides
          </p>
        </div>

        {/* Downloadable Materials */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold mb-8">Downloadable Resources</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courseMaterials.map((material) => {
              const IconComponent = material.icon;
              return (
                <Card key={material.id} className="hover:shadow-lg transition-all duration-300">
                  <CardHeader className="pb-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <IconComponent className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <CardTitle className="text-lg leading-tight">{material.title}</CardTitle>
                          <Badge variant="outline" className={`mt-1 ${getCategoryColor(material.category)}`}>
                            {material.category}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="space-y-4">
                    <p className="text-muted-foreground text-sm">{material.description}</p>
                    
                    <div className="flex items-center justify-between text-sm">
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium">Type:</span>
                          <Badge variant="secondary">{material.type}</Badge>
                        </div>
                        <div className="flex items-center space-x-4 text-muted-foreground">
                          <span>{material.size}</span>
                          <span>•</span>
                          <span>{material.files} files</span>
                        </div>
                      </div>
                    </div>
                    
                    <Button 
                      className="w-full" 
                      onClick={() => handleDownload(material)}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* External Resources */}
        <div>
          <h3 className="text-2xl font-bold mb-8">Additional Resources</h3>
          <div className="grid md:grid-cols-2 gap-6">
            {externalResources.map((resource, index) => (
              <Card key={index} className="hover:shadow-lg transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-lg mb-2">{resource.title}</h4>
                      <p className="text-muted-foreground text-sm mb-3">{resource.description}</p>
                      <Badge variant="outline" className="mb-4">
                        {resource.type}
                      </Badge>
                    </div>
                  </div>
                  <Button variant="outline" className="w-full" asChild>
                    <a href={resource.url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Visit Resource
                    </a>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Download All Section */}
        <div className="mt-16 text-center">
          <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold mb-4">Download Complete Course Package</h3>
              <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                Get all course materials in one convenient package. Includes slides, code, datasets, 
                assignments, and reference materials.
              </p>
              <div className="flex justify-center items-center space-x-8 mb-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">171 MB</div>
                  <div className="text-sm text-muted-foreground">Total Size</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">76</div>
                  <div className="text-sm text-muted-foreground">Files Included</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">ZIP</div>
                  <div className="text-sm text-muted-foreground">Format</div>
                </div>
              </div>
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                onClick={() => alert('Downloading complete package... (This is a demo)')}
              >
                <Download className="h-5 w-5 mr-2" />
                Download All Materials
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};